/**
 * Executes a single automation rule action via the writeback interface.
 *
 * All data mutations go through updateRecord (the writeback hook).
 * The only exception is 'notify' which uses the toast system directly.
 */

import type { AutomationAction, FieldMapping } from '@/types/pm-types';
import { resolveSpecialToken, type TokenResolutionContext } from './automationTokenResolver';

export interface ActionExecutorContext {
  updateRecord: (
    recordId: string,
    fieldChanges: Record<string, unknown>,
    options?: { onOptimisticUpdate?: () => void; onRollback?: () => void },
  ) => Promise<boolean>;
  toast: (opts: {
    title: string;
    description?: string;
    variant?: 'default' | 'destructive' | 'success';
  }) => void;
  currentUser?: string;
  previousRecord?: Record<string, unknown>;
  triggerRecord: Record<string, unknown>;
  fieldMapping?: FieldMapping;
  idField: string;
  writebackEnabled: boolean;
}

export interface ActionExecutionResult {
  success: boolean;
  error?: string;
  warning?: string;
}

export async function executeAction(
  action: AutomationAction,
  record: Record<string, unknown>,
  context: ActionExecutorContext,
): Promise<ActionExecutionResult> {
  const { config } = action;
  const recordId = String(record[context.idField] ?? '');

  const tokenCtx: TokenResolutionContext = {
    currentUser: context.currentUser,
    previousValue: context.previousRecord?.[config.field ?? ''],
    triggerRecord: context.triggerRecord,
  };

  // notify works without writeback
  if (action.type === 'notify') {
    return executeNotify(config, context);
  }

  // All other actions require writeback
  if (!context.writebackEnabled) {
    return {
      success: false,
      warning: 'Writeback disabled — data-modifying action skipped',
    };
  }

  if (!recordId) {
    return {
      success: false,
      error: `Cannot execute action: record ID field "${context.idField}" is empty`,
    };
  }

  try {
    switch (action.type) {
      case 'setField':
        return await executeSetField(recordId, config, tokenCtx, context);

      case 'setFieldToToday':
        // Convenience alias: delegates to setField with @today
        return await executeSetField(
          recordId,
          { ...config, value: '@today' },
          tokenCtx,
          context,
        );

      case 'moveToColumn':
        return await executeMoveToColumn(recordId, config, context);

      case 'addTag':
        return await executeAddTag(recordId, config, record, context);

      case 'removeTag':
        return await executeRemoveTag(recordId, config, record, context);

      case 'archive':
        return await executeArchive(recordId, context);

      case 'setFieldFromField':
        return await executeSetFieldFromField(recordId, config, record, context);

      default:
        return { success: false, error: `Unknown action type: "${action.type}"` };
    }
  } catch (err) {
    return {
      success: false,
      error: `Action "${action.type}" failed: ${err instanceof Error ? err.message : String(err)}`,
    };
  }
}

async function executeSetField(
  recordId: string,
  config: AutomationAction['config'],
  tokenCtx: TokenResolutionContext,
  context: ActionExecutorContext,
): Promise<ActionExecutionResult> {
  const field = config.field;
  if (!field) return { success: false, error: 'setField: missing field in config' };

  const resolvedValue = resolveSpecialToken(config.value, tokenCtx);
  const ok = await context.updateRecord(recordId, { [field]: resolvedValue });
  return ok
    ? { success: true }
    : { success: false, error: `setField: writeback failed for field "${field}"` };
}

async function executeMoveToColumn(
  recordId: string,
  config: AutomationAction['config'],
  context: ActionExecutorContext,
): Promise<ActionExecutionResult> {
  const column = config.column;
  if (!column) return { success: false, error: 'moveToColumn: missing column in config' };

  const columnField = context.fieldMapping?.column ?? 'status';
  const ok = await context.updateRecord(recordId, { [columnField]: column });
  return ok
    ? { success: true }
    : { success: false, error: `moveToColumn: writeback failed` };
}

async function executeAddTag(
  recordId: string,
  config: AutomationAction['config'],
  record: Record<string, unknown>,
  context: ActionExecutorContext,
): Promise<ActionExecutionResult> {
  const tag = config.tag;
  const field = config.field ?? 'tags';
  if (!tag) return { success: false, error: 'addTag: missing tag in config' };

  const currentTags = Array.isArray(record[field]) ? [...(record[field] as unknown[])] : [];
  if (currentTags.includes(tag)) return { success: true }; // Already has tag

  currentTags.push(tag);
  const ok = await context.updateRecord(recordId, { [field]: currentTags });
  return ok
    ? { success: true }
    : { success: false, error: `addTag: writeback failed` };
}

async function executeRemoveTag(
  recordId: string,
  config: AutomationAction['config'],
  record: Record<string, unknown>,
  context: ActionExecutorContext,
): Promise<ActionExecutionResult> {
  const tag = config.tag;
  const field = config.field ?? 'tags';
  if (!tag) return { success: false, error: 'removeTag: missing tag in config' };

  const currentTags = Array.isArray(record[field]) ? [...(record[field] as unknown[])] : [];
  const filtered = currentTags.filter((t) => t !== tag);
  const ok = await context.updateRecord(recordId, { [field]: filtered });
  return ok
    ? { success: true }
    : { success: false, error: `removeTag: writeback failed` };
}

async function executeArchive(
  recordId: string,
  context: ActionExecutorContext,
): Promise<ActionExecutionResult> {
  const ok = await context.updateRecord(recordId, { _archived: true });
  return ok
    ? { success: true }
    : { success: false, error: 'archive: writeback failed' };
}

function executeNotify(
  config: AutomationAction['config'],
  context: ActionExecutorContext,
): ActionExecutionResult {
  const message = config.message ?? 'Automation rule executed';
  let variant: 'default' | 'destructive' | 'success' = 'default';
  let title = message;

  if (config.notificationType === 'error') {
    variant = 'destructive';
  } else if (config.notificationType === 'success') {
    variant = 'success';
  } else if (config.notificationType === 'warning') {
    // Toast API has no 'warning' variant; use 'destructive' with prefix to distinguish from error
    variant = 'destructive';
    title = `Warning: ${message}`;
  }

  context.toast({ title, variant });
  return { success: true };
}

async function executeSetFieldFromField(
  recordId: string,
  config: AutomationAction['config'],
  record: Record<string, unknown>,
  context: ActionExecutorContext,
): Promise<ActionExecutionResult> {
  const targetField = config.field;
  const sourceField = config.sourceField;
  if (!targetField || !sourceField) {
    return { success: false, error: 'setFieldFromField: missing field or sourceField in config' };
  }

  const sourceValue = record[sourceField];
  const ok = await context.updateRecord(recordId, { [targetField]: sourceValue });
  return ok
    ? { success: true }
    : { success: false, error: `setFieldFromField: writeback failed` };
}
